import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle } from "lucide-react";
import { RocSignalLabel } from "@/components/roc/RocSignalLabel";

interface MethodologyEditorialProps {
  language: 'es' | 'en';
}

/**
 * Editorial explanation of the ROC Search Growth Framework: prioritization
 * logic, what happens per phase, common mistakes and how progress is
 * measured. No promised outcomes, timelines or guaranteed rankings.
 */
export const MethodologyEditorial = ({ language }: MethodologyEditorialProps) => {
  const content = {
    es: {
      overline: "Lógica de decisión",
      title: "Cómo se prioriza dentro del framework",
      intro:
        "Las siete fases (investigación, base técnica, intención de búsqueda, autoridad, visibilidad, conversión y medición) son la ejecución visible de un marco de decisión más simple: priorizar por impacto potencial, esfuerzo requerido y evidencia disponible, y medir cada cambio antes de escalarlo.",
      tableTitle: "Cómo se prioriza el trabajo",
      tableCaption: "Cada iniciativa se evalúa en tres ejes antes de entrar al plan de trabajo.",
      tableHeaders: ["Criterio", "Qué se evalúa", "Ejemplo"],
      tableRows: [
        ["Impacto", "Volumen de búsqueda, valor comercial de la intención y páginas afectadas", "Corregir el indexado de la categoría principal vs. una página de blog aislada"],
        ["Esfuerzo", "Complejidad técnica, dependencias de desarrollo y trabajo estimado", "Cambiar un meta tag vs. rediseñar la arquitectura de URLs"],
        ["Evidencia", "Qué tan respaldada está la hipótesis por datos (Search Console, rastreo, análisis de logs)", "Una caída de tráfico confirmada en GSC vs. una sospecha sin datos"],
      ],
      phasesTitle: "Qué ocurre en cada bloque",
      phases: [
        { name: "Investigación y base técnica", text: "Se define el problema real con datos existentes y se confirma que el sistema de búsqueda puede leer el sitio antes de invertir en contenido." },
        { name: "Intención y autoridad", text: "Se mapea qué consulta corresponde a qué página y qué señales de confianza faltan para competir en ese espacio." },
        { name: "Visibilidad y conversión", text: "Se ejecuta lo priorizado en lotes verificables, con criterios de éxito definidos antes de empezar cada uno." },
        { name: "Medición", text: "Se revisa qué cambios tuvieron el efecto esperado sobre la línea base y esa evidencia vuelve a la fase de investigación." },
      ],
      mistakesTitle: "Errores comunes que este marco busca evitar",
      mistakes: [
        "Ejecutar SEO técnico sin confirmar primero que el problema es de rastreo/indexación y no de contenido o demanda",
        "Priorizar por lo que es más fácil de hacer en vez de por impacto comercial real",
        "Lanzar cambios grandes sin una línea base de datos para poder medir el efecto después",
        "Tratar el SEO internacional como una traducción literal de palabras clave",
      ],
      measurementTitle: "Cómo se mide el progreso",
      measurementText:
        "El progreso se mide contra la línea base específica de cada sitio: visibilidad orgánica en las páginas priorizadas, estado de indexación, y señales de negocio cuando hay acceso a analítica (tráfico cualificado, conversiones asistidas). No se prometen posiciones ni plazos porque dependen de factores fuera del control de cualquier consultor: competencia, cambios de algoritmo y decisiones internas del cliente.",
      relatedTitle: "Recursos relacionados",
      relatedSubtitle: "Profundiza en las áreas donde este marco se aplica con más frecuencia.",
      ctaTitle: "¿Quieres ver cómo aplicaría este marco a tu sitio?",
      ctaText: "Una sesión de diagnóstico 1:1 identifica en qué fase está tu proyecto y qué prioridades tienen más sentido.",
      ctaButton: "Solicitar sesión de diagnóstico",
    },
    en: {
      overline: "Decision logic",
      title: "How work gets prioritized inside the framework",
      intro:
        "The seven phases (research, technical foundation, search intent, authority, visibility, conversion and measurement) are the visible execution of a simpler decision framework: prioritize by potential impact, required effort and available evidence, and measure every change before scaling it.",
      tableTitle: "How work gets prioritized",
      tableCaption: "Every initiative is evaluated on three axes before it enters the work plan.",
      tableHeaders: ["Criterion", "What is evaluated", "Example"],
      tableRows: [
        ["Impact", "Search volume, commercial value of intent and pages affected", "Fixing indexing on the main category vs. a single isolated blog post"],
        ["Effort", "Technical complexity, engineering dependencies and estimated work", "Changing a meta tag vs. redesigning the URL architecture"],
        ["Evidence", "How well the hypothesis is backed by data (Search Console, crawl data, log analysis)", "A confirmed traffic drop in GSC vs. a hunch with no data"],
      ],
      phasesTitle: "What happens in each block",
      phases: [
        { name: "Research and technical foundation", text: "The real problem is defined with existing data, and the search system's ability to read the site is confirmed before investing in content." },
        { name: "Intent and authority", text: "Queries are mapped to pages, and the missing trust signals for competing in that space are identified." },
        { name: "Visibility and conversion", text: "Prioritized work is executed in verifiable batches, with success criteria defined before each one starts." },
        { name: "Measurement", text: "Changes are reviewed against the baseline, and that evidence flows back into the research phase." },
      ],
      mistakesTitle: "Common mistakes this framework avoids",
      mistakes: [
        "Running technical SEO work before confirming the problem is actually crawling/indexing, not content or demand",
        "Prioritizing by ease of execution instead of real commercial impact",
        "Shipping large changes without a data baseline to measure the effect afterward",
        "Treating international SEO as a literal keyword translation exercise",
      ],
      measurementTitle: "How progress is measured",
      measurementText:
        "Progress is measured against each site's specific baseline: organic visibility on prioritized pages, indexing status, and business signals when analytics access is available (qualified traffic, assisted conversions). No specific rankings or timelines are promised because they depend on factors outside any consultant's control: competition, algorithm changes and the client's own internal decisions.",
      relatedTitle: "Related resources",
      relatedSubtitle: "Go deeper into the areas where this framework is applied most often.",
      ctaTitle: "Want to see how this framework would apply to your site?",
      ctaText: "A 1:1 diagnosis session identifies what phase your project is in and which priorities make the most sense.",
      ctaButton: "Request a diagnosis session",
    },
  } as const;

  const t = content[language];

  const relatedLinks = language === 'es'
    ? [
        { to: "/consultor-seo", label: "Consultor SEO", description: "Estrategia SEO senior para negocios B2B." },
        { to: "/servicios/seo-tecnico", label: "SEO técnico", description: "Priorización de arreglos de rastreo e indexación." },
        { to: "/servicios/seo-internacional", label: "SEO internacional", description: "Estrategia multi-mercado y hreflang." },
        { to: "/servicios/seo-bilingue-mercado-hispano", label: "SEO bilingüe / mercado hispano", description: "Estrategia para el consumidor hispano en EE. UU." },
        { to: "/biblioteca", label: "Biblioteca de búsqueda", description: "Guías detalladas sobre priorización, SEO técnico y AI search." },
      ]
    : [
        { to: "/en/seo-consultant", label: "SEO Consultant", description: "Senior SEO strategy for B2B businesses." },
        { to: "/en/services/technical-seo", label: "Technical SEO", description: "Prioritized crawling and indexing fixes." },
        { to: "/en/services/international-seo", label: "International SEO", description: "Multi-market strategy and hreflang." },
        { to: "/en/services/hispanic-market-seo", label: "Hispanic-market SEO", description: "Strategy for the US Hispanic consumer." },
        { to: "/en/library", label: "Search Library", description: "In-depth guides on prioritization, technical SEO and AI search." },
      ];

  const diagnosisPath = language === 'es' ? '/diagnostico-seo-gratis' : '/en/free-seo-diagnosis';

  return (
    <section className="bg-background py-16 lg:py-24">
      <div className="container-custom space-y-16">
        <div className="max-w-3xl">
          <RocSignalLabel index="02">{t.overline}</RocSignalLabel>
          <h2 className="font-display mt-4 text-2xl font-bold tracking-tight text-foreground lg:text-4xl">{t.title}</h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground lg:text-lg">{t.intro}</p>
        </div>

        <div>
          <h3 className="font-display text-xl font-bold text-foreground lg:text-2xl">{t.tableTitle}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{t.tableCaption}</p>
          <div className="mt-6 overflow-x-auto border border-border">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr className="border-b border-border">
                  {t.tableHeaders.map((h) => (
                    <th key={h} className="p-4 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.tableRows.map((row) => (
                  <tr key={row[0]} className="border-b border-border last:border-b-0">
                    {row.map((cell, i) => (
                      <td key={i} className={`p-4 align-top text-sm ${i === 0 ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h3 className="font-display text-xl font-bold text-foreground lg:text-2xl">{t.phasesTitle}</h3>
          <ul className="mt-6 border-t border-border">
            {t.phases.map((phase, index) => (
              <li key={phase.name} className="grid gap-2 border-b border-border py-5 sm:grid-cols-[64px_240px_1fr] sm:gap-6">
                <span className="font-mono text-[11px] text-brand">{String(index + 1).padStart(2, '0')}</span>
                <span className="text-sm font-semibold text-foreground">{phase.name}</span>
                <span className="text-sm leading-relaxed text-muted-foreground">{phase.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-border p-6 lg:p-8">
          <h3 className="flex items-center gap-3 font-display text-lg font-bold text-foreground">
            <AlertTriangle className="h-4 w-4 text-brand" aria-hidden="true" />
            {t.mistakesTitle}
          </h3>
          <ul className="mt-5 space-y-3">
            {t.mistakes.map((m) => (
              <li key={m} className="flex gap-4 text-sm leading-relaxed text-muted-foreground">
                <span className="mt-2 h-px w-4 flex-shrink-0 bg-brand" aria-hidden="true" />
                {m}
              </li>
            ))}
          </ul>
        </div>

        <div className="max-w-3xl">
          <h3 className="font-display text-xl font-bold text-foreground lg:text-2xl">{t.measurementTitle}</h3>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground lg:text-base">{t.measurementText}</p>
        </div>

        <div>
          <h3 className="font-display text-xl font-bold text-foreground lg:text-2xl">{t.relatedTitle}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{t.relatedSubtitle}</p>
          <ul className="mt-6 border-t border-border">
            {relatedLinks.map((link) => (
              <li key={link.to} className="border-b border-border">
                <Link
                  to={link.to}
                  className="group flex flex-col gap-1 py-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand sm:flex-row sm:items-baseline sm:gap-8"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-foreground sm:w-[320px]">
                    {link.label}
                    <ArrowRight className="h-4 w-4 text-brand transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                  <span className="text-sm leading-relaxed text-muted-foreground">{link.description}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-border p-8 lg:p-10">
          <h3 className="font-display max-w-2xl text-2xl font-bold text-foreground">{t.ctaTitle}</h3>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{t.ctaText}</p>
          <Link
            to={diagnosisPath}
            className="roc-link mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            {t.ctaButton}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};
